import moment from "moment";
import { getTokenFromLS } from "../account";
import { Configuration } from "../configuration";
import { axiosInstance } from "../context/axiosInstance";
import { FetchError, getError } from "../utils";

type PostData = { [key: string]: any };

type isError = {
	ok: boolean;
	status?: number;
	statusText?: string;
};

type FormattedParameter = {
	name?: string;
	value?: any;
};

type ExportColumn = {
	columnName?: string;
	caption?: string;
	order?: number;
};

type ExportSheet = {
	order?: number;
	resultSetIndex?: number;
	sheetName?: string;
	showAllColumns?: boolean;
	columns?: ExportColumn[];
};

type ExportStructure = {
	showAllSheets?: boolean;
	sheets?: ExportSheet[];
};

export type ResponseMESF =
	| {
			ok: false;
			message: string;
	  }
	| {
			ok: true;
			data: any;
	  };

const base =
	document.getElementsByTagName("base")[0].getAttribute("href") || "/";

export type spParameter = {
	name: string;
	value: string | number | boolean | null | Date;
};

export class MESApiService {
	private config: Configuration;
	// @ts-ignore
	public hasErrors: boolean;
	// @ts-ignore
	public error: any;
	private TOKEN: string;

	constructor() {
		this.config = new Configuration();
		this.hasErrors = false;
		this.error = undefined;
		this.TOKEN = this.config.TOKEN;
	}
	prepareCall() {
		this.hasErrors = false;
		this.error = undefined;
	}

	async changePassword(userId: string, password: string) {
		this.prepareCall();
		let isError: isError = { ok: true };
		const data: PostData = {};
		data.userId = userId;
		data.password = password;
		return fetch(this.config.API_AUTH_URL + "/ChangePassword", {
			method: "POST",
			mode: "cors",
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + getTokenFromLS(),
			},
			body: JSON.stringify(data),
		})
			.catch((errorMsg) => {
				throw new FetchError(errorMsg.message, 404, "ERR_CONNECTION_REFUSED");
			})
			.then((response) => {
				isError = {
					ok: response.ok,
					status: response.status,
					statusText: response.statusText,
				};
				return response.json();
			})
			.then((object) => {
				if (!isError.ok)
					throw new FetchError(
						<string>isError.statusText,
						<number>isError.status,
						object,
					);
				return object;
			})
			.catch((err) => {
				this.hasErrors = true;
				this.error = err;
				return {
					hasErrors: true,
					error: err,
				};
			});
	}
	async authenticate(userName: string, password: string) {
		this.prepareCall();
		let isError: isError = { ok: true };
		const data: PostData = {};
		data.userName = userName;
		data.password = password;
		return fetch(this.config.API_AUTH_URL + "/Authenticate", {
			method: "POST",
			mode: "cors",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(data),
		})
			.catch((errorMsg) => {
				throw new FetchError(errorMsg.message, 404, "ERR_CONNECTION_REFUSED");
			})
			.then((response) => {
				isError = {
					ok: response.ok,
					status: response.status,
					statusText: response.statusText,
				};
				return response.json();
			})
			.then((object) => {
				if (!isError.ok)
					throw new FetchError(
						<string>isError.statusText,
						<number>isError.status,
						object,
					);
				return object;
			})
			.catch((err) => {
				this.hasErrors = true;
				this.error = err;
				return {
					hasErrors: true,
					error: err,
				};
			});
	}

	async loginWithAzureAD(accessToken: string) {
		this.prepareCall();
		let isError: isError = { ok: true };
		const data: PostData = {};
		data.AccessToken = accessToken;
		return fetch(this.config.API_AUTH_URL + "/authenticate/azure-ad", {
			method: "POST",
			mode: "cors",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(data),
		})
			.catch((errorMsg) => {
				throw new FetchError(errorMsg.message, 404, "ERR_CONNECTION_REFUSED");
			})
			.then((response) => {
				isError = {
					ok: response.ok,
					status: response.status,
					statusText: response.statusText,
				};
				return response.json();
			})
			.then((object) => {
				if (!isError.ok)
					throw new FetchError(
						<string>isError.statusText,
						<number>isError.status,
						object,
					);
				return object;
			})
			.catch((err) => {
				this.hasErrors = true;
				this.error = err;
				return {
					hasErrors: true,
					error: err,
				};
			});
	}

	async loginWithWindowsAuthentication() {
		return fetch(this.config.API_AUTH_URL + "/authenticate/windows", {
			method: "POST",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		});
	}

	async uploadFiles(selectedFiles: File[], folderName = "") {
		const formData = new FormData();

		for (const file of selectedFiles) {
			formData.append("files", file, file.name);
		}

		formData.append("folderName", folderName);

		return fetch(this.config.API_PROCEDURE_URL + "/uploadFiles", {
			method: "POST",
			headers: {
				Authorization: "Bearer " + this.TOKEN,
			},
			body: formData,
		}).then((res) => res.json());
	}

	async call(procedure: string, parameters: any[], database = "") {
		const data: PostData = {};
		const formattedParameters: FormattedParameter[] = [];
		parameters.forEach((element: any) => {
			const formattedParameter: FormattedParameter = {};
			formattedParameter.name = element.name;
			if (element.value === null || element.value === undefined)
				formattedParameter.value = null;
			else formattedParameter.value = element.value;
			formattedParameters.push(formattedParameter);
		});
		data.method = procedure;
		data.dataBaseName = database;
		data.parameters = formattedParameters;
		Date.prototype.toJSON = function () {
			return moment(this).format("YYYY-MM-DDTHH:mm:ss");
		}; // eslint-disable-line
		let isError: isError = { ok: true };
		return fetch(this.config.API_PROCEDURE_URL + "/Execute", {
			method: "POST",
			mode: "cors",
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + getTokenFromLS(),
			},
			body: JSON.stringify(data),
		})
			.catch((errorMsg) => {
				throw new FetchError(errorMsg.message, 404, "ERR_CONNECTION_REFUSED");
			})
			.then((response) => {
				if (response.status === 499 || response.status === 401) {
					window.location.href = `${base}logout?message=Session%20expired&redirectTo=${window.location.href}`;
				}
				isError = {
					ok: response.ok,
					status: response.status,
					statusText: response.statusText,
				};
				return response.json();
			})
			.then((object) => {
				if (!isError.ok)
					throw new FetchError(
						<string>isError.statusText,
						<number>isError.status,
						object,
					);
				return object;
			})
			.catch((err) => {
				this.hasErrors = true;
				this.error = err;
				return;
			});
	}

	async callV2(
		procedure: string,
		parameters: any[],
		database = "",
	): Promise<ResponseMESF> {
		const data: PostData = {};
		const formattedParameters: FormattedParameter[] = [];
		parameters.forEach((element: any) => {
			const formattedParameter: FormattedParameter = {};
			formattedParameter.name = element.name;
			if (element.value === null || element.value === undefined)
				formattedParameter.value = null;
			else formattedParameter.value = element.value;
			formattedParameters.push(formattedParameter);
		});
		data.method = procedure;
		data.dataBaseName = database;
		data.parameters = formattedParameters;
		Date.prototype.toJSON = function () {
			return moment(this).format("YYYY-MM-DDTHH:mm:ss");
		}; // eslint-disable-line

		const curatedMethod = procedure.replace(/[\[\]]/g, ""); // remove brackets from method name
		try {
			const response = await axiosInstance.post(
				`/action/${curatedMethod}`,
				data,
				{
					params: data.parameters.map((param: any) => {
						let mapKey = param.name.replace("@", "");
						if (typeof param.value == "string" && param.value.length > 100) {
							return {
								[mapKey]: "__truncated__",
							};
						}

						return {
							[mapKey]: param.value,
						};
					}),
				},
			);
			return { ok: true, data: response.data };
		} catch (err: any) {
			this.hasErrors = true;
			this.error = err;
			return { ok: false, message: getError(err) };
		}
	}

	async callJSON(procedure: string, parameters: any[], database = "") {
		const data: PostData = {};
		const formattedParameters: FormattedParameter[] = [];
		parameters.forEach((element: any) => {
			const formattedParameter: FormattedParameter = {};
			formattedParameter.name = element.name;
			if (element.value === null || element.value === undefined)
				formattedParameter.value = null;
			else formattedParameter.value = element.value;
			formattedParameters.push(formattedParameter);
		});
		data.method = procedure;
		data.dataBaseName = database;
		data.parameters = formattedParameters;
		Date.prototype.toJSON = function () {
			return moment(this).format("YYYY-MM-DDTHH:mm:ss");
		}; // eslint-disable-line
		let isError: isError = { ok: true };
		return fetch(this.config.API_PROCEDURE_URL + "/ExecuteJSON", {
			method: "POST",
			mode: "cors",
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + getTokenFromLS(),
			},
			body: JSON.stringify(data),
		})
			.catch((errorMsg) => {
				throw new FetchError(errorMsg.message, 404, "ERR_CONNECTION_REFUSED");
			})
			.then((response) => {
				if (response.status === 499 || response.status === 401) {
					window.location.href = `${base}logout?message=Session%20expired&redirectTo=${window.location.href}`;
				}
				isError = {
					ok: response.ok,
					status: response.status,
					statusText: response.statusText,
				};
				return response.json();
			})
			.then((object) => {
				if (!isError.ok)
					throw new FetchError(
						<string>isError.statusText,
						<number>isError.status,
						object,
					);
				return object;
			})
			.catch((err) => {
				this.hasErrors = true;
				this.error = err;
				return;
			});
	}
	async import(
		procedure: string,
		parameters: any[],
		files: any[],
		uniqueFile: any,
		database = "",
	) {
		const formData = new FormData();
		const formattedParameters: FormattedParameter[] = [];
		parameters.forEach((element) => {
			const formattedParameter: FormattedParameter = {};
			formattedParameter.name = element.name;
			if (element.value === null || element.value === undefined)
				formattedParameter.value = null;
			else formattedParameter.value = element.value;
			formattedParameters.push(formattedParameter);
		});
		Date.prototype.toJSON = function () {
			return moment(this).format("YYYY-MM-DDTHH:mm:ss");
		}; // eslint-disable-line
		let isError: isError = { ok: true };
		files.forEach((element) => {
			formData.append("formFiles", element);
		});

		formData.append("method", procedure);
		formData.append("dataBaseName", database);
		formData.append("processFirstFile", uniqueFile);
		formData.append("parameters", JSON.stringify(formattedParameters));
		return fetch(this.config.API_PROCEDURE_URL + "/Import", {
			method: "POST",
			mode: "cors",
			headers: {
				Authorization: "Bearer " + getTokenFromLS(),
			},
			body: formData,
		})
			.catch((errorMsg) => {
				throw new FetchError(errorMsg.message, 404, "ERR_CONNECTION_REFUSED");
			})
			.then((response) => {
				if (response.status === 499 || response.status === 401) {
					window.location.href = `${base}logout?message=Session%20expired&redirectTo=${window.location.href}`;
				}
				isError = {
					ok: response.ok,
					status: response.status,
					statusText: response.statusText,
				};
				return response.json();
			})
			.then((object) => {
				if (!isError.ok)
					throw new FetchError(
						<string>isError.statusText,
						<number>isError.status,
						object,
					);
				return object;
			})
			.catch((err) => {
				this.hasErrors = true;
				this.error = err;
				return;
			});
	}
	async export(
		procedure: string,
		parameters: spParameter[],
		fileName: string,
		database = "",
	) {
		const data: { [key: string]: any } = {};
		const formattedParameters: FormattedParameter[] = [];
		parameters.forEach((element) => {
			const formattedParameter: FormattedParameter = {};
			formattedParameter.name = element.name;
			if (element.value === null || element.value === undefined)
				formattedParameter.value = null;
			else formattedParameter.value = element.value;
			formattedParameters.push(formattedParameter);
		});
		data.method = procedure;
		data.dataBaseName = database;
		data.parameters = formattedParameters;
		data.fileName = fileName || "Default.xlsx";
		Date.prototype.toJSON = function () {
			return moment(this).format("YYYY-MM-DDTHH:mm:ss");
		}; // eslint-disable-line
		let isError: isError = { ok: true };
		return fetch(this.config.API_PROCEDURE_URL + "/Export", {
			method: "POST",
			mode: "cors",
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + getTokenFromLS(),
			},
			body: JSON.stringify(data),
		})
			.catch((errorMsg) => {
				throw new FetchError(errorMsg.message, 404, "ERR_CONNECTION_REFUSED");
			})
			.then((response) => {
				if (response.status === 499 || response.status === 401) {
					window.location.href = `${base}logout?message=Session%20expired&redirectTo=${window.location.href}`;
				}
				isError = {
					ok: response.ok,
					status: response.status,
					statusText: response.statusText,
				};
				if (!isError.ok) return response.json();
				else return response.blob();
			})
			.then((blob) => {
				if (!isError.ok)
					throw new FetchError(
						<string>isError.statusText,
						<number>isError.status,
						blob,
					);
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = fileName || "Default.xlsx";
				document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
				a.click();
				a.remove(); //afterwards we remove the element again
				return "ok";
			})
			.catch((err) => {
				this.hasErrors = true;
				this.error = err;
				return;
			});
	}
	async exportExcel(
		procedure: string,
		parameters: spParameter[],
		fileName: string,
		excelStructure: ExportStructure,
		database = "",
	) {
		const data: { [key: string]: any } = {};
		/******* Parameters *******/
		const formattedParameters: FormattedParameter[] = [];
		parameters.forEach((element) => {
			const formattedParameter: FormattedParameter = {};
			formattedParameter.name = element.name;
			if (element.value === null || element.value === undefined)
				formattedParameter.value = null;
			else formattedParameter.value = element.value;
			formattedParameters.push(formattedParameter);
		});
		/******* Excel Structure *******/
		if (
			excelStructure.showAllSheets !== undefined &&
			excelStructure.showAllSheets !== null
		)
			data.showAllSheets = excelStructure.showAllSheets;
		if (excelStructure.sheets !== undefined && excelStructure.sheets !== null)
			data.sheets = excelStructure.sheets;
		/******* Other Parameter *******/
		data.method = procedure;
		data.dataBaseName = database;
		data.parameters = formattedParameters;
		data.fileName = fileName || "Default.xlsx";
		Date.prototype.toJSON = function () {
			return moment(this).format("YYYY-MM-DDTHH:mm:ss");
		}; // eslint-disable-line
		let isError: isError = { ok: true };
		return fetch(this.config.API_PROCEDURE_URL + "/ExportExcel", {
			method: "POST",
			mode: "cors",
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + getTokenFromLS(),
			},
			body: JSON.stringify(data),
		})
			.catch((errorMsg) => {
				throw new FetchError(errorMsg.message, 404, "ERR_CONNECTION_REFUSED");
			})
			.then((response) => {
				if (response.status === 499 || response.status === 401) {
					window.location.href = `${base}logout?message=Session%20expired&redirectTo=${window.location.href}`;
				}
				isError = {
					ok: response.ok,
					status: response.status,
					statusText: response.statusText,
				};
				if (!isError.ok) return response.json();
				else return response.blob();
			})
			.then((blob) => {
				if (!isError.ok)
					throw new FetchError(
						<string>isError.statusText,
						<number>isError.status,
						blob,
					);
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = fileName || "Default.xlsx";
				document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
				a.click();
				a.remove(); //afterwards we remove the element again
				return "ok";
			})
			.catch((err) => {
				this.hasErrors = true;
				this.error = err;
				return;
			});
	}

	async checkIfWindowsAuthIsEnabled() {
		try {
			const resp = await fetch(
				`${this.config.API_AUTH_URL}/is-windows-auth-enabled`,
			).then((res) => res.json());
			return resp;
		} catch (e) {
			return false;
		}
	}
}
